import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { COURSE_CONTENT_NAME } from "../metaCourse";
import { metaPixelService } from "../services/metaPixelService";
import { hasSensitiveSearch } from "../utils/analyticsUrl";
import { readMarketingConsent } from "../utils/cookieConsent";
import { getAuthErrorMessage } from "../utils/firebaseAuthErrors";
import BrandMark from "../components/BrandMark";
import FullScreenLoader from "../components/FullScreenLoader";
import googleIcon from "../assets/google-icon.webp";
import "../styles/auth.css";

interface AuthPageProps {
  mode: "signup" | "signin";
}

const AuthPage: React.FC<AuthPageProps> = ({ mode }) => {
  const { user, loading, signUp, signIn, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState<"email" | "google" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isSignUp = mode === "signup";

  const trackRegistration = () => {
    if (!readMarketingConsent() || hasSensitiveSearch(window.location.search)) return;
    metaPixelService.track("CompleteRegistration", {
      content_name: COURSE_CONTENT_NAME,
      status: true,
    });
  };

  if (loading) {
    return <FullScreenLoader message="Checking authentication..." />;
  }

  if (user) {
    return <Navigate to="/home" replace />;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (isSignUp && password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting("email");
    try {
      if (isSignUp) {
        await signUp(email, password);
        trackRegistration();
      } else {
        await signIn(email, password);
      }
    } catch (err) {
      setError(getAuthErrorMessage(err));
    } finally {
      setSubmitting(null);
    }
  };

  const handleGoogle = async () => {
    setError(null);
    setSubmitting("google");
    try {
      const isNewUser = await signInWithGoogle();
      if (isNewUser) trackRegistration();
    } catch (err) {
      const message = getAuthErrorMessage(err);
      if (message !== "Sign-in was cancelled.") {
        setError(message);
      }
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div className="min-h-screen bg-base-200">
      <div className="fixed top-0 left-0 right-0 bg-base-100 border-b border-base-200 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" aria-label="PEAK home" className="shrink-0 hover:opacity-80 transition-opacity">
            <BrandMark />
          </Link>
          <div className="text-sm text-base-content/80">
            <strong>{isSignUp ? "Sign Up" : "Login"}</strong>
          </div>
        </div>
      </div>

      <div className="mx-auto px-8 max-w-xl pt-24 py-16">
        <form className="grid grid-cols-1 gap-3 mt-4" onSubmit={handleSubmit}>
          <div className="space-y-1 mb-4">
            <h2 className="text-2xl font-bold font-grotesk">
              {isSignUp ? "Create an account" : "Sign In"}
            </h2>
            <p className="text-sm text-base-content/80">
              {isSignUp ? "Sign up to get started" : "Welcome back"}
            </p>
          </div>

          {error && (
            <div className="p-4 mb-4 bg-red-500/15 text-red-700 border border-red-500/30 rounded-md">
              {error}
            </div>
          )}

          <div className="form-control">
            <div className="new-form-group w-full">
              <input
                className="form-element"
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={submitting !== null}
                placeholder="Enter your email"
              />
              <label htmlFor="email" className="text-base-content/80">
                Email Address
              </label>
            </div>
          </div>

          <div className="form-control">
            <div className="new-form-group w-full">
              <input
                className="form-element"
                id="password"
                name="password"
                type="password"
                autoComplete={isSignUp ? "new-password" : "current-password"}
                required
                minLength={6}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={submitting !== null}
                placeholder="Enter your password"
              />
              <label htmlFor="password" className="text-base-content/80">
                Password
              </label>
            </div>
          </div>

          {isSignUp && (
            <div className="form-control">
              <div className="new-form-group w-full">
                <input
                  className="form-element"
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={6}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  disabled={submitting !== null}
                  placeholder="Re-enter your password"
                />
                <label htmlFor="confirmPassword" className="text-base-content/80">
                  Confirm Password
                </label>
              </div>
            </div>
          )}

          <div className="mt-10">
            <button
              type="submit"
              className="w-full text-center p-4 py-2.5 rounded-md transition-colors bg-black text-white disabled:opacity-60"
              disabled={submitting !== null}
            >
              <h3 className="flex justify-center items-center gap-2 font-semibold">
                {submitting === "email" ? (
                  <>
                    <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    {isSignUp ? "Creating account…" : "Signing in…"}
                  </>
                ) : isSignUp ? (
                  "Sign Up"
                ) : (
                  "Sign In"
                )}
              </h3>
            </button>
          </div>

          <div className="flex items-center gap-2 justify-center text-sm text-base-content/80 mt-4">
            <div className="w-full border-t border-base-300" />
            <p>Or</p>
            <div className="w-full border-t border-base-300" />
          </div>

          <button
            type="button"
            onClick={handleGoogle}
            disabled={submitting !== null}
            className="py-3 flex items-center gap-2 justify-center bg-base-100 border border-base-300 w-full mt-2 text-base-content/80 hover:bg-base-200 transition-colors rounded-md disabled:opacity-60"
          >
            <img src={googleIcon} alt="" className="w-5 h-5" />
            {submitting === "google" ? "Continuing…" : "Continue with Google"}
          </button>

          <div className="text-center mt-4">
            {isSignUp ? (
              <Link
                to="/login"
                className="text-base-content/80 hover:text-base-content text-sm transition-colors"
              >
                I already have an account. Login
              </Link>
            ) : (
              <Link
                to="/signup"
                className="text-base-content/80 hover:text-base-content text-sm transition-colors"
              >
                I don't have an account. Sign Up
              </Link>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AuthPage;
